import React, { HTMLAttributes, useState } from 'react';

import { Switch } from './switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Spinner } from './spinner';

type ViewEditDialogProps = HTMLAttributes<HTMLDivElement> & {
  title: string;
  onSubmit?: () => void | Promise<void>;
  onClose?: () => void;
  render: ({
    isEditMode,
    setIsSubmitting,
  }: {
    isEditMode: boolean;
    setIsSubmitting: (isSubmitting: boolean) => void;
  }) => React.ReactNode;
  initEditMode?: boolean;
  newEntityCreation?: boolean;
};

export function ViewEditDialog({
  title,
  render,
  onSubmit,
  onClose,
  initEditMode = false,
  newEntityCreation = false,
}: ViewEditDialogProps) {
  const [open, setOpen] = useState(true);

  const [isEditMode, setIsEditMode] = useState(initEditMode);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      await onSubmit?.();
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      return;
    }

    handleOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    if (!open) onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="block max-h-[90vh] w-[90vw] max-w-none overflow-y-auto rounded-lg border-gray-200 p-6 shadow-lg md:w-[80vw] lg:w-[60vw] lg:max-w-[800px] lg:p-6">
        <DialogHeader className="mb-10">
          <DialogTitle className="text-left">{title}</DialogTitle>

          {!newEntityCreation && (
            <div className="absolute right-14 top-2 z-10 flex items-center gap-2">
              <span className="text-sm font-medium transition-all duration-300">
                {isEditMode ? 'Edit Mode' : 'View Mode'}
              </span>

              <Switch
                id="edit-mode-switch"
                checked={isEditMode}
                onCheckedChange={setIsEditMode}
              />
            </div>
          )}
        </DialogHeader>

        {render({ isEditMode, setIsSubmitting })}

        {isEditMode && (
          <div className="mt-4 flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsEditMode(false);
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => {
                void handleSubmit();
              }}
            >
              {isSubmitting ? <Spinner /> : 'Save'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
